import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (p1 !== p2) {
      toast.error("Passwords don't match");
      return;
    }
    if (p1.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: p1 });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen auth-bg grid place-items-center p-6">
        <Card className="w-full max-w-md border-border shadow-none">
          <CardContent className="p-8 text-center" data-testid="reset-missing-token">
            <h2 className="font-display text-xl font-bold">Missing reset token</h2>
            <p className="text-sm text-muted-foreground mt-2">
              This page expects a reset link from your email. Request a new one if needed.
            </p>
            <Link to="/forgot-password" className="inline-block mt-6 text-sm font-medium underline">
              Request a new reset link
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen auth-bg grid place-items-center p-6 sm:p-10">
      <Card className="w-full max-w-md border-border shadow-none" data-testid="reset-password-card">
        <CardContent className="p-8">
          <div className="tiny-label">Account recovery</div>
          <h2 className="font-display text-2xl font-bold mt-2">Set a new password</h2>
          <p className="text-sm text-muted-foreground mt-1">Pick something memorable but strong (min 8 characters).</p>

          {done ? (
            <div className="mt-8 space-y-4" data-testid="reset-success">
              <div className="flex items-start gap-3 p-4 rounded-md bg-emerald-50 border border-emerald-200 text-sm">
                <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="text-emerald-900">
                  Password updated. Redirecting to sign-in…
                </div>
              </div>
            </div>
          ) : (
            <form className="mt-8 space-y-5" onSubmit={submit}>
              <div>
                <Label htmlFor="p1">New password</Label>
                <div className="relative mt-1.5">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input id="p1" type="password" required minLength={8}
                    value={p1} onChange={(e) => setP1(e.target.value)}
                    className="h-11 pl-9" autoComplete="new-password"
                    data-testid="reset-pw1-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="p2">Confirm new password</Label>
                <Input id="p2" type="password" required minLength={8}
                  value={p2} onChange={(e) => setP2(e.target.value)}
                  className="h-11 mt-1.5" autoComplete="new-password"
                  data-testid="reset-pw2-input" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-11" data-testid="reset-submit-btn">
                {submitting ? "Updating…" : "Update password"}
              </Button>
              <Link to="/login" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-3 h-3 mr-1" /> Back to sign in
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
